import { useMemo, type ReactNode } from 'react';
import { BrowserRouter, MemoryRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CssBaseline, ThemeProvider, useMediaQuery } from '@mui/material';
import { makeTheme } from './theme';
import { AuthProvider, useAuth, useIsOwner } from './auth/AuthProvider';
import { TotpPromptProvider } from './auth/TotpPrompt';
import { NotifyProvider } from './components/Notify';
import { Layout } from './components/Layout';
import { LoginPage, SplashScreen } from './pages/Login';
import { ClonesPage, OverviewPage } from './pages/Overview';
import { ClonePage } from './pages/clone/ClonePage';
import { NewCloneWizard } from './pages/NewCloneWizard';
import { JobPage } from './pages/JobPage';
import { ReleasesPage } from './pages/Releases';
import { AlertsPage } from './pages/Alerts';
import { SettingsPage } from './pages/Settings';
import { AccountPage } from './pages/Account';
import { EmptyState } from './components/common';
import { isApiError } from './api/client';

export function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 10_000,
        refetchOnWindowFocus: true,
        retry: (n, e) => !(isApiError(e) && e.status >= 400 && e.status < 500) && n < 2,
      },
      mutations: { retry: false },
    },
  });
}

function OwnerOnly({ children }: { children: ReactNode }) {
  return useIsOwner() ? <>{children}</> : <EmptyState title="Owners only" text="Ask an owner to change your role." />;
}

export function AppRoutes() {
  const { status } = useAuth();
  if (status === 'loading') return <SplashScreen />;
  if (status !== 'ok') return <LoginPage />;
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<OverviewPage />} />
        <Route path="clones" element={<ClonesPage />} />
        <Route path="clones/new" element={<OwnerOnly><NewCloneWizard /></OwnerOnly>} />
        <Route path="clones/:id/:tab?" element={<ClonePage />} />
        <Route path="jobs/:id" element={<JobPage />} />
        <Route path="releases" element={<ReleasesPage />} />
        <Route path="alerts" element={<AlertsPage />} />
        <Route path="account" element={<AccountPage />} />
        <Route path="settings/:tab?" element={<OwnerOnly><SettingsPage /></OwnerOnly>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

/** Theme, data, auth and dialogs; the router is injectable for tests. */
export function Providers({ children, client, initialPath }: { children: ReactNode; client?: QueryClient; initialPath?: string }) {
  const dark = useMediaQuery('(prefers-color-scheme: dark)', { noSsr: true });
  const theme = useMemo(() => makeTheme(dark ? 'dark' : 'light'), [dark]);
  const qc = useMemo(() => client ?? makeQueryClient(), [client]);
  const Router = initialPath ? MemoryRouter : BrowserRouter;
  const routerProps = initialPath ? { initialEntries: [initialPath] } : {};
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline enableColorScheme />
      <QueryClientProvider client={qc}>
        <Router {...routerProps}>
          <NotifyProvider>
            <TotpPromptProvider>
              <AuthProvider>{children}</AuthProvider>
            </TotpPromptProvider>
          </NotifyProvider>
        </Router>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export function App() {
  return (
    <Providers>
      <AppRoutes />
    </Providers>
  );
}
